import React from 'react';

import {
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Group,
  Modal,
  Skeleton,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconExclamationCircle } from '@tabler/icons-react';
import BackButton from 'components/BackButton';
import PageHeader from 'components/PageHeader';
import Tabs from 'components/Tabs';
import { useAuth } from 'context/auth-context';
import useAthlete from 'hooks/athlete/useAthlete';
import useRequestPaymentInfoForAthlete from 'hooks/athlete/useRequestPaymentInfoForAthlete';
import useSendActivationEmail from 'hooks/users/useSendActivationEmail';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { AthleteRetrieve } from 'types';
import notificationHandler from 'utils/notifications';
import { isWorkoutTrackerEnabled } from 'utils/workoutTracker';

import AthleteActionsMenu from './components/AthleteActionsMenu';
import Attendance from './components/Attendance';
import Communications from './components/Communications';
import Credits from './components/Credits';
import Documents from './components/Documents';
import GeneralInfo from './components/GeneralInfo/GeneralInfo';
import Invoice from './components/Invoice';
import Notes from './components/Notes';
import Packages from './components/Packages';
import Reservations from './components/Reservations';
import UploadedFiles from './components/UploadedFiles';
import WorkoutTracker from './components/WorkoutTracker';
import DeleteAthleteModal from '../Athletes/components/DeleteAthleteModal';
import DrawerAthleteForm from '../Athletes/components/DrawerAthleteForm';
import UpdateAthleteNotificationsSettings from '../Athletes/components/UpdateAthleteNotificationsSettingModal';

const AthleteDetail = (): JSX.Element => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { successNotification, errorNotification } = notificationHandler();

  const { athleteId } = useParams<{ athleteId: string }>();

  const {
    data: athleteData,
    isLoading: isAthleteLoading,
    isError: isAthleteError,
    isSuccess: isAthleteSuccess,
  } = useAthlete(athleteId);

  const [isEditFormOpened, { open: openEditForm, close: closeEditForm }] = useDisclosure(false);
  const [
    isCreateLinkedProfileFormOpened,
    { open: openCreateLinkedProfileForm, close: closeCreateLinkedProfileForm },
  ] = useDisclosure(false);
  const [
    isCreateDependentProfileFormOpened,
    { open: openCreateDependentProfileForm, close: closeCreateDependentProfileForm },
  ] = useDisclosure(false);
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [isNotificationsModalOpen, { open: openNotificationsModal, close: closeNotificationsModal }] =
    useDisclosure(false);

  const { session, hasPerm } = useAuth();

  const athletesPerm = hasPerm('AthleteMutate');
  const eventMutatePerm = hasPerm('EventMutate');
  const athletePackageMutatePerm = hasPerm('AthletePackageMutate');
  const invoiceAthleteViewPerm = hasPerm('InvoiceAthleteView');
  const issuedDocumentMutatePerm = hasPerm('IssuedDocumentMutate');

  const workoutTrackerEnabled = isWorkoutTrackerEnabled(session?.gym?.workoutTrackerEnabled);

  const sendActivationEmail = useSendActivationEmail();
  const requestPaymentInfo = useRequestPaymentInfoForAthlete();

  const getAthleteTabs = (
    athlete: AthleteRetrieve,
  ): {
    label: string;
    value: string;
    'data-cy'?: string;
    component: JSX.Element;
  }[] => {
    const arr = [
      {
        label: 'General Info',
        value: 'general-info',
        component: <GeneralInfo athlete={athlete} permission={athletesPerm} />,
      },
      {
        label: 'Packages',
        value: 'packages',
        component: <Packages athlete={athlete} permission={athletePackageMutatePerm} />,
      },
      {
        label: 'Documents',
        value: 'documents',
        component: <Documents athlete={athlete} permission={issuedDocumentMutatePerm} />,
      },
      {
        label: 'Invoices',
        value: 'invoices',
        component: <Invoice athlete={athlete} />,
        disabled: !invoiceAthleteViewPerm,
      },
      {
        label: 'Reservations',
        value: 'reservations',
        component: <Reservations athlete={athlete} permission={eventMutatePerm} />,
      },
      {
        label: 'Attendance',
        value: 'attendance',
        component: <Attendance athlete={athlete} permission={eventMutatePerm} />,
      },
      ...(workoutTrackerEnabled
        ? [
            {
              label: 'Workout Tracker',
              value: 'workouts',
              component: <WorkoutTracker athlete={athlete} />,
            },
          ]
        : []),
      {
        label: 'Credits',
        value: 'credits',
        component: <Credits athlete={athlete} permission={athletePackageMutatePerm} />,
      },
      {
        label: 'Notes',
        value: 'notes',
        component: <Notes athlete={athlete} permission={athletesPerm} />,
      },
      {
        label: 'Communications',
        value: 'communications',
        component: <Communications athlete={athlete} />,
      },
      {
        label: 'Uploaded files',
        value: 'files',
        component: <UploadedFiles athlete={athlete} permission={athletesPerm} />,
      },
    ];
    return arr;
  };

  const handleSendLoginSetupEmail = (athlete: AthleteRetrieve): void => {
    sendActivationEmail.mutate(athlete.user, {
      onSuccess: () => {
        successNotification('Login setup email successfully sent');
      },
      onError: () => {
        errorNotification('Login setup email failed to send');
      },
    });
  };

  const handleRequestPaymentInfo = (athlete: AthleteRetrieve): void => {
    requestPaymentInfo.mutate(athlete.athleteId, {
      onSuccess: () => {
        queryClient.invalidateQueries(['communications', athlete.athleteId]);
        successNotification(
          'Payment request sent',
          <>
            An email was sent to {athlete.email}.{' '}
            <Anchor
              component='button'
              type='button'
              onClick={() => navigate(`/users/athletes/${athlete.athleteId}/communications`)}
            >
              View in Communications.
            </Anchor>
          </>,
        );
      },
      onError: error => {
        errorNotification('Payment request failed to send', error.response?.data?.message);
      },
    });
  };

  const handleOnClick = (): void => {
    navigate(-1);
  };

  if (isAthleteError) {
    return (
      <Center sx={{ flexGrow: 1 }} p='lg'>
        <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
          Something went wrong while fetching data!
        </Alert>
      </Center>
    );
  }

  return (
    <>
      <PageHeader withoutBorder>
        <Group position='apart' noWrap>
          <Group position='left' spacing='xl' noWrap>
            <BackButton handleOnClick={handleOnClick} />
            {isAthleteLoading && <Skeleton height='2.8rem' sx={{ width: '30rem' }} />}
            {isAthleteSuccess && athleteData && (
              <>
                <Title order={1}>{athleteData.data.fullName}</Title>
                {athleteData.data.connectedType === 'Linked' && (
                  <Badge color='kiloBlue' variant='filled' radius='md'>
                    Linked
                  </Badge>
                )}
                {athleteData.data.connectedType === 'Dependent' && (
                  <Badge color='kiloBlue' variant='filled' radius='md'>
                    Dependent
                  </Badge>
                )}
                {!athleteData.data.loginEnabled && (
                  <Badge color='red' variant='filled'>
                    Login Disabled
                  </Badge>
                )}
              </>
            )}
          </Group>
          <Group position='right'>
            {isAthleteLoading && (
              <Skeleton
                height={`calc(${theme.spacing.xl} * 2)`}
                sx={{ width: `calc(${theme.spacing.xl} * 4)` }}
              />
            )}
            {isAthleteSuccess && (
              <AthleteActionsMenu
                athleteRetrieve={athleteData.data}
                handleEdit={openEditForm}
                handleDelete={openDeleteModal}
                handleSendLoginSetupEmail={handleSendLoginSetupEmail}
                handleRequestPaymentInfo={handleRequestPaymentInfo}
                handleCreateLinkedProfile={openCreateLinkedProfileForm}
                handleCreateDependentProfile={openCreateDependentProfileForm}
                handleNotificationsSettings={openNotificationsModal}
                target={<Button disabled={!athletesPerm}>Actions</Button>}
                disabled={!athletesPerm}
                wodupEnabled={!!session?.gym?.wodupEnabled}
              />
            )}
          </Group>
        </Group>
      </PageHeader>
      {isAthleteSuccess && athleteData?.data && (
        <Tabs
          styles={{
            root: { overflowY: 'auto', flexGrow: 1, display: 'flex', flexDirection: 'column' },
            tabsList: { flexGrow: 0 },
            panel: { overflowY: 'auto', flexGrow: 1, display: 'flex', flexDirection: 'column' },
          }}
          pathPattern='/users/athletes/:athleteId/:activeTab?'
          color='primary'
          items={getAthleteTabs(athleteData.data)}
        />
      )}
      <DrawerAthleteForm
        type={
          isEditFormOpened
            ? 'edit'
            : isCreateLinkedProfileFormOpened
            ? 'create-linked'
            : isCreateDependentProfileFormOpened
            ? 'create-dependent'
            : 'closed'
        }
        opened={isEditFormOpened || isCreateLinkedProfileFormOpened || isCreateDependentProfileFormOpened}
        closeHandler={
          isEditFormOpened
            ? closeEditForm
            : isCreateLinkedProfileFormOpened
            ? closeCreateLinkedProfileForm
            : closeCreateDependentProfileForm
        }
        athleteId={isEditFormOpened ? athleteId : undefined}
        primaryAthleteId={isEditFormOpened ? undefined : athleteId}
      />
      <Modal
        opened={isDeleteModalOpen}
        onClose={closeDeleteModal}
        title={<Title order={4}>Delete Athlete</Title>}
        trapFocus
        centered
        size='auto'
      >
        <DeleteAthleteModal athleteId={athleteId} handleClose={closeDeleteModal} redirect />
      </Modal>
      <Modal
        opened={isNotificationsModalOpen}
        onClose={closeNotificationsModal}
        title={<Title order={4}>Notifications Settings</Title>}
        trapFocus
        centered
        size='auto'
        sx={{
          '.mantine-Modal-header': {
            borderBottom: `1px solid ${theme.colors.gray[3]}`,
          },
        }}
      >
        <UpdateAthleteNotificationsSettings athleteId={athleteId} handleClose={closeNotificationsModal} />
      </Modal>
    </>
  );
};

export default AthleteDetail;
